// OPS POS — Offline / IndexedDB helpers (app side)
// Shared with sw.js by convention — keep in sync manually.
'use strict';

const DB_NAME   = 'ops-offline';
const DB_VER    = 2; // bumped for cart store
const ORDER_ST  = 'queued_orders';
const CART_ST   = 'saved_cart';

function openDb() {
  return new Promise((res, rej) => {
    const req = indexedDB.open(DB_NAME, DB_VER);
    req.onupgradeneeded = e => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(ORDER_ST)) {
        db.createObjectStore(ORDER_ST, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(CART_ST)) {
        db.createObjectStore(CART_ST, { keyPath: 'key' });
      }
    };
    req.onsuccess = e => res(e.target.result);
    req.onerror   = e => rej(e.target.error);
  });
}

// --- Cart persistence ---

export async function saveCartToIdb(cart, cartDiscount, selectedCustomer, paymentMethod) {
  try {
    const db = await openDb();
    await new Promise((res, rej) => {
      const tx  = db.transaction(CART_ST, 'readwrite');
      const req = tx.objectStore(CART_ST).put({
        key: 'active',
        cart, cartDiscount, selectedCustomer, paymentMethod,
        savedAt: Date.now(),
      });
      req.onsuccess = () => res();
      req.onerror   = e => rej(e.target.error);
    });
  } catch { /* non-fatal */ }
}

export async function loadCartFromIdb() {
  try {
    const db = await openDb();
    return await new Promise((res, rej) => {
      const tx  = db.transaction(CART_ST, 'readonly');
      const req = tx.objectStore(CART_ST).get('active');
      req.onsuccess = e => res(e.target.result || null);
      req.onerror   = e => rej(e.target.error);
    });
  } catch { return null; }
}

export async function clearCartFromIdb() {
  try {
    const db = await openDb();
    await new Promise((res, rej) => {
      const tx  = db.transaction(CART_ST, 'readwrite');
      const req = tx.objectStore(CART_ST).delete('active');
      req.onsuccess = () => res();
      req.onerror   = e => rej(e.target.error);
    });
  } catch { /* non-fatal */ }
}

// --- Queued orders ---

export async function getQueuedOrders() {
  try {
    const db = await openDb();
    return await new Promise((res, rej) => {
      const tx  = db.transaction(ORDER_ST, 'readonly');
      const req = tx.objectStore(ORDER_ST).getAll();
      req.onsuccess = e => res(e.target.result || []);
      req.onerror   = e => rej(e.target.error);
    });
  } catch { return []; }
}

// --- Service Worker registration ---

export function registerServiceWorker(onOffline, onOnline, onSyncDone) {
  if (!('serviceWorker' in navigator)) return;

  navigator.serviceWorker.register('/sw.js', { scope: '/' }).then(reg => {
    console.log('[SW] registered', reg.scope);
  }).catch(err => console.warn('[SW] registration failed', err));

  navigator.serviceWorker.addEventListener('message', e => {
    if (e.data?.type === 'SYNC_DONE') onSyncDone?.();
  });

  window.addEventListener('offline', () => onOffline?.());
  window.addEventListener('online',  () => {
    onOnline?.();
    // Trigger background sync
    navigator.serviceWorker.ready.then(reg => {
      if ('sync' in reg) {
        reg.sync.register('sync-orders').catch(() => {});
      } else {
        // Fallback: tell SW to sync manually
        navigator.serviceWorker.controller?.postMessage('SYNC_NOW');
      }
    });
  });
}
